import { Link } from 'react-router';
import zigzagSvg from '@/assets/zigzag.svg';
import { DecorativeSvgMask } from '@/components/DecorativeSvgMask';
import type { HubEndCard } from '@/components/Hub/hubSectionTypes';
import { focusRingClassNames } from '@/components/ui/utils';
import { trackingAttributes } from '@/lib/heap/trackingAttributes';
import { cn } from '@/lib/utils';
import 'material-symbols';

/** Wider than the card so the strokes run off every edge rather than sitting inside it as a motif. */
const ZIGZAG_SIZE = '140% auto';

// Inline because the `type-*` utilities are emitted after Tailwind's own, so a `font-bold` or
// `leading-*` class beside one is silently overridden.
const HEADING_STYLE = { fontWeight: 700 } as const;
const LABEL_STYLE = { lineHeight: '1.375rem' } as const;

export interface HubSectionEndCardProps {
    endCard: HubEndCard;
    /** The owning section's id and title, used for the analytics names. */
    sectionId: string;
    sectionTitle: string;
}

// Both themes pick a light accent ground, so the text stays dark in both.
export const HubSectionEndCard = ({ endCard, sectionId, sectionTitle }: HubSectionEndCardProps) => (
    <Link
        to={endCard.to}
        className={cn(
            // Fixed height so the card matches in both sections whatever sits beside it.
            'group relative flex h-[265px] flex-col justify-between gap-4 overflow-hidden rounded-xl p-6',
            endCard.groundClassName,
            'text-gz-neutral-900',
            focusRingClassNames,
        )}
        {...trackingAttributes({
            click: `Hub ${sectionTitle} End Card Clicked`,
            location: 'hub-sheet',
            buttonName: `hub-${sectionId}-end-card`,
        })}
    >
        <DecorativeSvgMask
            src={zigzagSvg}
            maskFillClassName={endCard.zigzagClassName}
            opacityClassName={endCard.zigzagOpacityClassName}
            maskSize={ZIGZAG_SIZE}
        />
        <span className="relative z-10 flex flex-col gap-3">
            <span className="type-subheading-large" style={HEADING_STYLE}>{endCard.heading}</span>
            <span className="type-body-large" style={LABEL_STYLE}>{endCard.actionLabel}</span>
        </span>
        {/* The disc is the hover affordance, and hugs the arrow the way the design draws it: the
            glyph keeps its own size and the disc is that plus the border it has to make room for. */}
        <span
            className={cn(
                'material-symbols-rounded relative z-10 flex size-11 items-center justify-center self-end rounded-full !text-[42px]',
                'border border-transparent transition-colors',
                'group-hover:border-gz-neutral-200 group-hover:bg-white',
                'dark:group-hover:border-gz-blue-700 dark:group-hover:bg-gz-blue-900 dark:group-hover:text-white',
            )}
            aria-hidden
        >
            arrow_forward
        </span>
    </Link>
);
